import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Alert, Autocomplete,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { customerSponsorApi, Customer, Sponsor } from '../../api/customerSponsorApi';
import { primaryButtonSx } from '../../theme/tokens';
import { WriteButton } from '../../components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (sponsor: Sponsor) => void;
  /** Preselect (and lock) the parent customer — used from the tenant wizard. */
  defaultCustomerId?: string | null;
  lockCustomer?: boolean;
}

/**
 * Create a new platform sponsor under a customer. Additive only — safe direct
 * platform write (no publish staging). A parent customer is required (FK).
 */
export default function CreateSponsorDialog({ open, onClose, onCreated, defaultCustomerId, lockCustomer }: Props) {
  const queryClient = useQueryClient();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['lookup-customers'],
    queryFn: async () => { const { data } = await axiosClient.get('/api/lookup/customers'); return data; },
    enabled: open,
  });

  // Preselect the parent customer once the list loads (wizard passes one in).
  useEffect(() => {
    if (!open || !defaultCustomerId || customer) return;
    const match = customers.find((c) => String(c.customer_id) === String(defaultCustomerId));
    if (match) setCustomer(match);
  }, [open, defaultCustomerId, customers, customer]);

  const reset = () => { setCustomer(null); setName(''); setDescription(''); setError(null); setSubmitting(false); };
  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!customer) { setError('Select a parent customer'); return; }
    if (!trimmed) { setError('Sponsor name is required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const created = await customerSponsorApi.createSponsor({
        customer_id: String(customer.customer_id),
        sponsor_name: trimmed,
        sponsor_description: description.trim() || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['lookup-sponsors'] });
      onCreated?.(created);
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to create sponsor');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create Sponsor</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            options={customers}
            value={customer}
            onChange={(_, v) => setCustomer(v)}
            disabled={lockCustomer && !!customer}
            getOptionLabel={(o) => `${o.customer_name} (${o.customer_code.slice(0, 20)}…)`}
            isOptionEqualToValue={(o, v) => o.customer_id === v.customer_id}
            renderInput={(params) => (
              <TextField {...params} label="Parent Customer *" size="small" helperText="Sponsor belongs to this customer" />
            )}
          />
          <TextField
            label="Sponsor Name *"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            helperText="Display name for the sponsor"
          />
          <TextField
            label="Description"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText="Optional — defaults to the sponsor name"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <WriteButton ignoreEnvReadOnly onClick={handleSubmit} disabled={submitting || !customer || !name.trim()} variant="contained" sx={{ ...primaryButtonSx }}>
          {submitting ? 'Creating…' : 'Create Sponsor'}
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
}
